import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { MessageCircle, Mail, MapPin, Clock, ArrowRight } from "lucide-react";
import { SERVICIOS } from "@/components/Servicios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INITIAL = {
    tipo_persona: "Natural",
    nombre: "",
    nit_cedula: "",
    correo: "",
    telefono: "",
    servicio_interes: SERVICIOS[0].titulo,
    mensaje: "",
};

const inputCls =
    "w-full bg-transparent border border-white/15 px-4 py-3.5 text-sm text-[#F8F9FA] placeholder:text-[#9CA3AF]/50 focus:border-gold focus:outline-none transition-colors duration-300";
const labelCls = "mb-2 block text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF]";

const CONTACTO = [
    { icon: MessageCircle, label: "WhatsApp", valor: "+57 304 396 1777", testid: "contact-whatsapp" },
    { icon: Mail, label: "Correo", valor: "gerencia@asconsultingsas.com", testid: "contact-email" },
    { icon: MapPin, label: "Dirección", valor: "Cra. 5 #22-78, San Nicolás · Cali", testid: "contact-address" },
    { icon: Clock, label: "Horario", valor: "Lun a vie · 8:00 a.m. – 6:00 p.m. · Sáb · 8:00 a.m. – 4:00 p.m.", testid: "contact-hours" },
];

export default function Contacto() {
    const [form, setForm] = useState(INITIAL);
    const [sending, setSending] = useState(false);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const onSubmit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            await axios.post(`${API}/leads`, form);
            toast.success("Solicitud enviada correctamente", {
                description: "Te contactaremos en máximo un día hábil.",
            });
            setForm(INITIAL);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            const msg = Array.isArray(detail)
                ? detail.map((d) => d.msg).join(" · ")
                : typeof detail === "string"
                  ? detail
                  : "Revisa los datos e inténtalo de nuevo.";
            toast.error("No pudimos enviar tu solicitud", { description: msg });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contacto" className="relative bg-navy px-6 py-24 md:px-12 md:py-36" data-testid="contacto-section">
            <div className="mx-auto grid max-w-[1440px] grid-cols-1 gap-16 lg:grid-cols-2 lg:gap-24">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                >
                    <p className="mb-6 text-[11px] uppercase tracking-[0.3em] text-gold" data-testid="contacto-overline">
                        Capítulo 03 — Contacto
                    </p>
                    <h2
                        className="font-display text-4xl font-medium leading-[1.05] tracking-tighter sm:text-5xl lg:text-6xl"
                        data-testid="contacto-heading"
                    >
                        Hablemos de tu empresa.
                    </h2>
                    <p className="mt-8 max-w-md text-base leading-relaxed text-[#9CA3AF] md:text-lg">
                        Cuéntanos qué necesidad requieres y enseguida te
                        atenderemos. Un contador público te responderá
                        personalmente.
                    </p>

                    <div className="mt-14 space-y-8">
                        {CONTACTO.map(({ icon: Icon, label, valor, testid }) => (
                            <div key={label} className="flex items-start gap-5" data-testid={testid}>
                                <span className="flex h-11 w-11 shrink-0 items-center justify-center border border-gold/40 text-gold">
                                    <Icon size={18} />
                                </span>
                                <div>
                                    <p className="text-[10px] uppercase tracking-[0.25em] text-[#9CA3AF]">{label}</p>
                                    <p className="mt-1 text-base text-[#F8F9FA]">{valor}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.form
                    onSubmit={onSubmit}
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    className="border border-white/10 bg-navy-deep p-8 md:p-12"
                    data-testid="lead-form"
                >
                    <p className="mb-10 text-[11px] uppercase tracking-[0.3em] text-gold">Solicitud de asesoría</p>

                    <div className="grid grid-cols-1 gap-7 sm:grid-cols-2">
                        <div>
                            <label htmlFor="tipo_persona" className={labelCls}>Tipo de persona *</label>
                            <select
                                id="tipo_persona"
                                value={form.tipo_persona}
                                onChange={set("tipo_persona")}
                                className={inputCls}
                                style={{ colorScheme: "dark" }}
                                data-testid="lead-tipo-select"
                            >
                                <option value="Natural">Natural</option>
                                <option value="Jurídica">Jurídica</option>
                            </select>
                        </div>
                        <div>
                            <label htmlFor="servicio_interes" className={labelCls}>Servicio de interés *</label>
                            <select
                                id="servicio_interes"
                                value={form.servicio_interes}
                                onChange={set("servicio_interes")}
                                className={inputCls}
                                style={{ colorScheme: "dark" }}
                                data-testid="lead-servicio-select"
                            >
                                {SERVICIOS.map((s) => (
                                    <option key={s.titulo} value={s.titulo}>{s.titulo}</option>
                                ))}
                            </select>
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="nombre" className={labelCls}>Nombre completo o razón social *</label>
                            <input
                                id="nombre"
                                required
                                minLength={3}
                                value={form.nombre}
                                onChange={set("nombre")}
                                placeholder="Ej.: Comercializadora del Valle S.A.S."
                                className={inputCls}
                                data-testid="lead-nombre-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="nit_cedula" className={labelCls}>NIT o cédula *</label>
                            <input
                                id="nit_cedula"
                                required
                                value={form.nit_cedula}
                                onChange={set("nit_cedula")}
                                placeholder="Ej.: 901.456.789-1"
                                className={inputCls}
                                data-testid="lead-nit-input"
                            />
                        </div>
                        <div>
                            <label htmlFor="telefono" className={labelCls}>Teléfono / WhatsApp *</label>
                            <input
                                id="telefono"
                                required
                                minLength={7}
                                value={form.telefono}
                                onChange={set("telefono")}
                                placeholder="Ej.: 315 456 7890"
                                className={inputCls}
                                data-testid="lead-telefono-input"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="correo" className={labelCls}>Correo electrónico *</label>
                            <input
                                id="correo"
                                type="email"
                                required
                                value={form.correo}
                                onChange={set("correo")}
                                placeholder="tu@empresa.com"
                                className={inputCls}
                                data-testid="lead-correo-input"
                            />
                        </div>
                        <div className="sm:col-span-2">
                            <label htmlFor="mensaje" className={labelCls}>Mensaje</label>
                            <textarea
                                id="mensaje"
                                rows={4}
                                value={form.mensaje}
                                onChange={set("mensaje")}
                                placeholder="Cuéntanos brevemente tu situación o necesidad…"
                                className={`${inputCls} resize-none`}
                                data-testid="lead-mensaje-textarea"
                            />
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={sending}
                        className="group mt-10 flex w-full items-center justify-center gap-3 bg-gold px-8 py-4 text-[11px] font-semibold uppercase tracking-[0.22em] text-navy transition-colors duration-300 hover:bg-gold-soft disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="lead-submit-button"
                    >
                        {sending ? "Enviando…" : "Solicitar asesoría"}
                        {!sending && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
                    </button>

                    <p className="mt-6 text-[11px] leading-relaxed text-[#9CA3AF]/70" data-testid="lead-data-notice">
                        Al enviar este formulario autorizas el tratamiento de tus datos
                        personales conforme a la Ley 1581 de 2012 y el Decreto 1377 de 2013.
                        Tus datos solo se usarán para atender tu solicitud.
                    </p>
                </motion.form>
            </div>
        </section>
    );
}
